'use client';

import { useEffect, useRef } from 'react';
import { loadChoicesConstructor } from '../lib/third-party/choices';

type ChoiceOption = {
  label: string;
  value: string;
  disabled?: boolean;
  placeholder?: boolean;
};

type ChoiceGroup = {
  label: string;
  options: ChoiceOption[];
};

type ChoicesSelectProps = {
  id?: string;
  value: string;
  options: Array<ChoiceOption | ChoiceGroup>;
  disabled?: boolean;
  searchEnabled?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
};

type ChoicesInstance = {
  destroy: () => void;
  setChoiceByValue?: (value: string) => void;
};

export function ChoicesSelect({
  id,
  value,
  options,
  disabled = false,
  searchEnabled = false,
  placeholder,
  onChange,
}: ChoicesSelectProps) {
  const selectRef = useRef<HTMLSelectElement>(null);
  const choicesRef = useRef<ChoicesInstance | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const optionSignature = JSON.stringify(options);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const select = selectRef.current;

    if (!select) {
      return;
    }

    let mounted = true;
    const handleNativeChange = () => {
      const nextValue = select.value;
      if (nextValue !== valueRef.current) {
        valueRef.current = nextValue;
        onChangeRef.current(nextValue);
      }
    };

    select.addEventListener('change', handleNativeChange);

    async function initializeChoices() {
      const Choices = await loadChoicesConstructor();

      if (!mounted || !selectRef.current) {
        return;
      }

      choicesRef.current?.destroy();
      choicesRef.current = new Choices(selectRef.current, {
        allowHTML: true,
        itemSelectText: '',
        placeholder: Boolean(placeholder),
        placeholderValue: placeholder,
        searchEnabled,
        shouldSort: false,
      }) as ChoicesInstance;
      choicesRef.current.setChoiceByValue?.(valueRef.current);
    }

    void initializeChoices();

    return () => {
      mounted = false;
      select.removeEventListener('change', handleNativeChange);
      choicesRef.current?.destroy();
      choicesRef.current = null;
    };
  }, [optionSignature, placeholder, searchEnabled]);

  useEffect(() => {
    choicesRef.current?.setChoiceByValue?.(value);
  }, [value]);

  return (
    <select
      id={id}
      ref={selectRef}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.value)}
    >
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((option) => (
        'options' in option ? (
          <optgroup key={option.label} label={option.label}>
            {option.options.map((item) => (
              <option
                key={item.value}
                value={item.value}
                disabled={item.disabled}
                data-placeholder={item.placeholder ? 'true' : undefined}
              >
                {item.label}
              </option>
            ))}
          </optgroup>
        ) : (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            data-placeholder={option.placeholder ? 'true' : undefined}
          >
            {option.label}
          </option>
        )
      ))}
    </select>
  );
}
